import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../../../utils/api';
import { PartnerEndpoints } from '../../../../utils/endpoints';
import { todayISO, yesterdayISO } from '../../../../utils/datetime';
import { DASHBOARD_METRIC_KEYS } from '../../../../utils/permissions';

const MAX_CUSTOM_SPAN_DAYS = 366;
const MS_PER_DAY = 86400000;
const EXPIRY_WARNING_DAYS = 14;

const plural = (count, noun) => `${count} ${noun}${count === 1 ? '' : 's'}`;

const spanInDays = (from, to) =>
  (Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / MS_PER_DAY + 1;

export const validateParams = ({ granularity, date, from, to }) => {
  const today = todayISO();

  if (granularity !== 'custom') {
    return date && date > today ? 'The selected date cannot be in the future.' : null;
  }
  if (!from || !to) return 'Choose both a start and an end date for a custom range.';
  if (from > to) return 'The start date cannot be later than the end date.';
  if (from > today || to > today) return 'A custom range cannot include future dates.';
  if (spanInDays(from, to) > MAX_CUSTOM_SPAN_DAYS) {
    return `A custom range cannot span more than ${MAX_CUSTOM_SPAN_DAYS} days.`;
  }
  return null;
};

export const requestMessage = (requestError) => {
  const body = requestError?.response?.data;
  const message = typeof body === 'string' ? body : body?.message;
  return message || 'We could not load the dashboard. Please try again.';
};

const buildCoverageNote = (data) => {
  const coverage = data?.period?.coverage;
  const missingCount = coverage?.missing_dates?.length ?? 0;
  if (!coverage || missingCount === 0) return null;

  if (coverage.present_days === 0 && (data.today?.txn_count ?? 0) > 0) {
    return {
      stale: true,
      message:
        'Today is recording activity but no day in this period has been rolled up yet, so the figures below are out of date.',
    };
  }
  return {
    stale: false,
    message: `${coverage.present_days} of ${plural(coverage.expected_days, 'day')} so far recorded activity; ${plural(
      missingCount,
      'day'
    )} were quiet.`,
  };
};

const buildCredentialStatus = (data) => {
  const credentials = data?.operations?.credentials;
  const days = credentials?.days_to_expiry;
  if (days === null || days === undefined) return null;

  const expired = days < 0;
  const label = expired
    ? `Expired ${plural(Math.abs(days), 'day')} ago`
    : days === 0
      ? 'Expires today'
      : `Expires in ${plural(days, 'day')}`;

  return { expiresAt: credentials.expires_at, expired, label, needsAttention: days <= EXPIRY_WARNING_DAYS };
};

const humanize = (key) => {
  const words = key.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
};

const buildMetricOptions = (data) => {
  const labels = new Map(
    [...(data?.summary ?? []), ...(data?.issuing ?? []), ...(data?.acquiring ?? [])].map((metric) => [
      metric.key,
      metric.label,
    ])
  );
  return DASHBOARD_METRIC_KEYS.map((key) => ({ key, label: labels.get(key) ?? humanize(key) }));
};

const useDashboard = () => {
  const [params, setParams] = useState({
    granularity: 'monthly',
    date: yesterdayISO(),
    from: '',
    to: '',
    metric: 'total_gross_volume',
    top: 10,
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const latestRequest = useRef(0);

  const { granularity, date, from, to, metric, top } = params;
  const paramError = validateParams(params);

  const updateParams = useCallback((patch) => setParams((current) => ({ ...current, ...patch })), []);
  const refetch = useCallback(() => setReloadToken((token) => token + 1), []);

  useEffect(() => {
    if (paramError) {
      setLoading(false);
      return;
    }

    const requestId = latestRequest.current + 1;
    latestRequest.current = requestId;
    setLoading(true);
    setError(null);

    const query =
      granularity === 'custom'
        ? { granularity, from, to, metric, top }
        : { granularity, date, metric, top };

    api
      .get(PartnerEndpoints.dashboard, { params: query })
      .then((response) => {
        if (latestRequest.current !== requestId) return;
        setData(response?.data?.data ?? null);
        setLoading(false);
      })
      .catch((requestError) => {
        if (latestRequest.current !== requestId) return;
        setData(null);
        setError(requestMessage(requestError));
        setLoading(false);
      });
  }, [granularity, date, from, to, metric, top, paramError, reloadToken]);

  return {
    params,
    updateParams,
    refetch,
    data,
    loading,
    error,
    paramError,
    coverageNote: buildCoverageNote(data),
    credential: buildCredentialStatus(data),
    metricOptions: buildMetricOptions(data),
  };
};

export default useDashboard;
